import type { CellSamples } from "@redotech/admin-sdk/rpc/schema/rate-tables/rate-grid-schema";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import * as styles from "./cell-detail-modal.module.css";

interface CellDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  cellLabel: string;
  samples: CellSamples | null;
  totalOrders: number;
}

export const CellDetailModal = memo(function CellDetailModal({
  isOpen,
  onClose,
  cellLabel,
  samples,
  totalOrders,
}: CellDetailModalProps) {
  const sortedSamples = useMemo(() => {
    if (!samples?.samples) {
      return [];
    }
    return [...samples.samples].sort((a, b) => {
      // Sort by date desc, then by price
      if (a.date && b.date) {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
      if (a.date) {
        return -1;
      }
      if (b.date) {
        return 1;
      }
      return b.price - a.price;
    });
  }, [samples]);

  if (!samples) {
    return null;
  }

  const { stats, marginStats } = samples;
  const hasMarginData = marginStats && marginStats.count > 0;

  return (
    <RedoModal
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      title={`Shipment Details - ${cellLabel}`}
    >
      <Flex className={styles.modalContent} dir="column" gap="lg" p="lg">
        {/* Price Statistics Section */}
        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="semibold">
            Price Statistics ({totalOrders.toLocaleString()} total orders)
          </Text>
          <Flex gap="lg" wrap="wrap">
            <StatBox label="Min" value={`$${stats.min.toFixed(2)}`} />
            <StatBox label="Max" value={`$${stats.max.toFixed(2)}`} />
            <StatBox label="Average" value={`$${stats.avg.toFixed(2)}`} />
            <StatBox label="Median" value={`$${stats.median.toFixed(2)}`} />
            <StatBox label="Mode" value={`$${stats.mode.toFixed(2)}`} />
            <StatBox label="Std Dev" value={`$${stats.stdDev.toFixed(2)}`} />
          </Flex>
        </Flex>

        {/* Margin Statistics Section - only show if margin data is available */}
        {hasMarginData && (
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" fontWeight="semibold">
              Current Redo Margin ({marginStats.count.toLocaleString()} orders
              with data)
            </Text>
            <Flex gap="lg" wrap="wrap">
              <StatBox
                label="Min"
                textColor={marginStats.min < 0 ? "error" : "success"}
                value={`$${marginStats.min.toFixed(2)}`}
              />
              <StatBox
                label="Max"
                textColor="success"
                value={`$${marginStats.max.toFixed(2)}`}
              />
              <StatBox
                label="Average"
                textColor={marginStats.avg < 0 ? "error" : "success"}
                value={`$${marginStats.avg.toFixed(2)}`}
              />
              <StatBox
                label="Median"
                textColor={marginStats.median < 0 ? "error" : "success"}
                value={`$${marginStats.median.toFixed(2)}`}
              />
            </Flex>
            <Text fontSize="xs" textColor="secondary">
              Margin = What merchant paid Redo - What Redo paid carrier
            </Text>
          </Flex>
        )}

        {/* Sample Shipments Table */}
        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="semibold">
            Sample Shipments ({sortedSamples.length} of{" "}
            {totalOrders.toLocaleString()})
          </Text>
          <Flex
            borderColor="primary"
            borderRadius="lg"
            borderWidth="1px"
            className={styles.samplesTableContainer}
            overflow="auto"
          >
            <table className={styles.samplesTable}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Tracking</th>
                  <th className={styles.tableHeader}>Weight (oz)</th>
                  <th className={styles.tableHeader}>Zone</th>
                  <th className={styles.tableHeader}>Redo Cost</th>
                  {hasMarginData && (
                    <th className={styles.tableHeader}>Merch Paid</th>
                  )}
                  {hasMarginData && (
                    <th className={styles.tableHeader}>Redo Margin</th>
                  )}
                  <th className={styles.tableHeader}>Carrier</th>
                  <th className={styles.tableHeader}>Date</th>
                </tr>
              </thead>
              <tbody>
                {sortedSamples.map((sample, idx) => (
                  <tr key={idx}>
                    <td className={styles.tableCell}>
                      {sample.trackingNumber ? (
                        <Text className={styles.trackingNumber} fontSize="xs">
                          {sample.trackingNumber}
                        </Text>
                      ) : (
                        <Text fontSize="xs" textColor="secondary">
                          -
                        </Text>
                      )}
                    </td>
                    <td className={styles.tableCell}>
                      {sample.weightOz.toFixed(1)}
                    </td>
                    <td className={styles.tableCellCenter}>
                      {sample.zone || "-"}
                    </td>
                    <td className={styles.tableCellRight}>
                      ${sample.price.toFixed(2)}
                    </td>
                    {hasMarginData && (
                      <td className={styles.tableCellRight}>
                        {sample.merchantPaid != null
                          ? `$${sample.merchantPaid.toFixed(2)}`
                          : "-"}
                      </td>
                    )}
                    {hasMarginData && (
                      <td
                        className={
                          sample.currentRedoMargin != null
                            ? sample.currentRedoMargin < 0
                              ? styles.marginNegative
                              : styles.marginPositive
                            : styles.tableCellRight
                        }
                      >
                        {sample.currentRedoMargin != null
                          ? `$${sample.currentRedoMargin.toFixed(2)}`
                          : "-"}
                      </td>
                    )}
                    <td className={styles.tableCell}>
                      {sample.carrier || "-"}
                    </td>
                    <td className={styles.tableCell}>
                      {sample.date ? formatDate(sample.date) : "-"}
                    </td>
                  </tr>
                ))}
                {sortedSamples.length === 0 && (
                  <tr>
                    <td
                      className={styles.tableCellCenter}
                      colSpan={hasMarginData ? 8 : 6}
                    >
                      <Text fontSize="sm" textColor="secondary">
                        No sample data available
                      </Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Flex>
        </Flex>
      </Flex>
    </RedoModal>
  );
});

const StatBox = memo(function StatBox({
  label,
  value,
  textColor = "primary",
}: {
  label: string;
  value: string;
  textColor?: "primary" | "success" | "error";
}) {
  return (
    <Flex
      bgColor="secondary"
      borderColor="primary"
      borderRadius="lg"
      borderWidth="1px"
      className={styles.statBox}
      dir="column"
      gap="xs"
      p="sm"
    >
      <Text fontSize="xs" textColor="secondary">
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        textColor={textColor as "primary"}
      >
        {value}
      </Text>
    </Flex>
  );
});

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
    });
  } catch {
    return dateStr;
  }
}
